import { CanDeactivateFn } from '@angular/router';

/**
 * A page that can be holding work nobody has saved.
 *
 * Implemented by the component on the route, so the guard asks the screen
 * rather than reaching into a store it would then have to know the shape of.
 */
export interface HasUnsavedChanges {
  /** True while there is something that would be lost by leaving. */
  hasUnsavedChanges(): boolean;
  /** Ask. Resolves true to leave anyway, false to stay. */
  confirmDiscard(): Promise<boolean>;
}

/**
 * Stops a navigation that would throw away unsaved work, and asks first.
 *
 * **Why a guard rather than a warning on the page.** The page already said
 * "Unsaved changes" in amber beside the Save button, and somebody still built a
 * whole checklist, clicked into another section and lost it — because a label
 * is something you have to be looking at, and the moment that matters is the
 * one where your attention has already moved somewhere else. The only thing
 * that works is the navigation itself stopping.
 *
 * **It is deliberately not a save.** Leaving is sometimes exactly what somebody
 * means, and a guard that silently saved would be a different kind of surprise
 * — one where the half-finished thing is now real.
 *
 * Add it to a route with `canDeactivate: [unsavedChangesGuard]` and implement
 * `HasUnsavedChanges` on the component.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (component) => {
  if (!component?.hasUnsavedChanges?.()) return true;
  return component.confirmDiscard();
};
